import React, { useState } from "react";
import styled from "styled-components";
import { get, isArray, pickBy, uniqBy } from "lodash";
import { CheckboxIcon } from "@sonder/ui/source/styled/fragments/CheckboxIcon";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { LabeledIconButton } from "@sonder/ui/source/styled/fragments/LabeledIconButton";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { UpdateTasksResponse, useUpdateTasks } from "core/queries/UpdateTasks";
import {
  addBatchJob,
  addSequentialJob,
  deselectTasks,
  setSelecting,
} from "schedule/actions";
import { ScheduleHeader } from "schedule/components/ScheduleHeader";
import { useStore } from "schedule/store";
import {
  AssigneePicker,
  AssigneePickerTrigger,
} from "tasks/components/AssigneePicker";
import { isSpaceCleanTask } from "tasks/helpers";

const MismatchErrorStickyBanner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  color: ${colors.white};
  background-color: ${colors.redD};
  padding: 0.625rem;
  font-weight: bold;
  text-align: center;
  width: 100%;
`;

export const BulkActionsHeader = () => {
  const {
    state: { user },
  } = useAuthStore();
  const useSequentialScheduler = user?.featureFlags?.includes(
    "dispatch_enable_sequential_scheduler_updater",
  );

  const { track } = useSegment();
  const {
    dispatch,
    state: { selectedTasks },
  } = useStore();
  const taskId = "VARIOUS";
  const eventName = "scheduler_bulk_action";
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [onBulkUpdateTasks, { loading }] = useUpdateTasks({
    refetchQueries: [],
    onCompleted: (data: UpdateTasksResponse) => {
      const batchJobId = get(data, "updateTasks.batchJob.id");
      if (batchJobId) {
        dispatch(addBatchJob(batchJobId));
      }
    },
  });

  const taskTypes = uniqBy(selectedTasks, "type").map(t => t.type);
  const cleaningCompanyIds = uniqBy(selectedTasks, "cleaningCompanyId").map(
    t => t.cleaningCompanyId,
  );
  const areTasksSelected = selectedTasks.length > 0;
  const areTasksModified = assigneeIds.length > 0;
  const firstTaskType = taskTypes[0];
  const hasSpaceClean =
    taskTypes.filter(type => isSpaceCleanTask(type)).length > 0;
  const taskTypesMismatch = taskTypes.length > 1;
  const noSelectedTaskType = taskTypes.length === 0;
  const cleaningCompanyMismatch = cleaningCompanyIds.length > 1;
  const canSubmit =
    areTasksSelected &&
    areTasksModified &&
    !taskTypesMismatch &&
    !cleaningCompanyMismatch;

  // This is purely for rendering AssigneePicker correctly given Space Clean (single select) or Clean (multiple select)
  const transformedAssigneeIds = hasSpaceClean
    ? [assigneeIds[0]].filter(x => !!x)
    : assigneeIds;

  return (
    <ScheduleHeader>
      {!taskTypesMismatch && cleaningCompanyMismatch && (
        <MismatchErrorStickyBanner>
          Please select cleans that are assigned to a single company. Assigning
          employees from different companies to the same clean will fail.
        </MismatchErrorStickyBanner>
      )}
      {taskTypesMismatch && (
        <MismatchErrorStickyBanner>
          Please select only space cleans or unit cleans. Bulk assignees cannot
          be performed at the moment when the selection contains both types.
        </MismatchErrorStickyBanner>
      )}
      <InputGroup>
        <LabeledIconButton
          title="Select / Deselect"
          onClick={() => {
            track({
              eventName,
              action: "click",
              element: "deselectAllTasks",
              props: {
                taskId,
              },
            });
            dispatch(deselectTasks(selectedTasks));
            dispatch(setSelecting(false));
          }}
        >
          <CheckboxIcon checked={selectedTasks.length > 0} />
          <span>{`(${selectedTasks.length}) Selected`}</span>
        </LabeledIconButton>
        <AssigneePicker
          alignContent="left"
          assigneeIds={transformedAssigneeIds}
          companyIds={cleaningCompanyIds}
          disabled={taskTypesMismatch || noSelectedTaskType}
          eventName={eventName}
          renderTrigger={({ assignees, onClear }) => (
            <AssigneePickerTrigger
              assignees={assignees}
              disabled={taskTypesMismatch || noSelectedTaskType}
              onClear={() => {
                if (onClear) {
                  onClear();
                }
                setAssigneeIds([]);
              }}
            />
          )}
          taskCityId={undefined}
          taskId={taskId}
          taskType={firstTaskType}
          unassignCopy="Deselect All"
          onAssign={nextValue => {
            setAssigneeIds(
              isArray(nextValue)
                ? nextValue
                : [nextValue || ""].filter(x => !!x),
            );
          }}
        />
        <PrimaryButton
          disabled={loading || !canSubmit}
          onClick={async () => {
            const variables = pickBy(
              {
                assigneeId: hasSpaceClean ? assigneeIds[0] : undefined,
                assigneeIds: !hasSpaceClean ? assigneeIds : undefined,
              },
              p => p !== undefined,
            );
            if (useSequentialScheduler) {
              dispatch(
                addSequentialJob({
                  tasks: selectedTasks.map(task => ({
                    id: task?.id.split("|")[1],
                    internalRef: task?.internalRef,
                  })),
                  variables,
                }),
              );
            } else {
              await onBulkUpdateTasks({
                variables: {
                  taskIds: selectedTasks.map(task => task?.id.split("|")[1]),
                  ...variables,
                },
              });
            }
            track({
              eventName,
              action: "click",
              element: "submit_button",
              props: {
                taskId,
              },
            });
            dispatch(deselectTasks(selectedTasks));
            dispatch(setSelecting(false));
          }}
        >
          {loading ? "Saving..." : "Confirm"}
        </PrimaryButton>
      </InputGroup>
    </ScheduleHeader>
  );
};
